import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface ProgressChartProps {
  data: Array<{
    name: string;
    progress: number;
    current?: number;
    target?: number;
  }>;
  title?: string;
  color?: string;
}

export default function ProgressChart({ data, title, color = '#00d9ff' }: ProgressChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-cyber-text-muted">
        No progress data available
      </div>
    );
  }

  // Generate colors based on progress
  const getColor = (progress: number): string => {
    if (progress >= 100) return '#39ff14'; // Neon green for completed
    if (progress >= 70) return '#00d9ff'; // Cyan for on track
    if (progress >= 40) return '#ff00ff'; // Purple for moderate
    return '#ff006e'; // Pink for low
  };

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-lg font-display font-bold text-cyber-text-primary mb-4">
          {title}
        </h3>
      )}
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
          <XAxis
            type="number"
            domain={[0, 100]}
            tick={{ fill: '#8b9dc3', fontSize: 11 }}
            stroke="#2a2a4a"
            label={{ value: 'Progress %', position: 'bottom', fill: '#8b9dc3', fontSize: 12 }}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fill: '#8b9dc3', fontSize: 11 }}
            stroke="#2a2a4a"
            width={120}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#0a0a1a',
              border: `1px solid ${color}`,
              borderRadius: '8px',
              color: '#dfe6e9',
            }}
            formatter={(value: number, name: string, props: any) => {
              const item = props.payload;
              if (item.current !== undefined && item.target !== undefined) {
                return [
                  `${value.toFixed(1)}% (${item.current}/${item.target})`,
                  'Progress'
                ];
              }
              return [`${value.toFixed(1)}%`, 'Progress'];
            }}
          />
          <Bar dataKey="progress" radius={[0, 4, 4, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getColor(entry.progress)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
